import { useEffect, useRef } from 'react'

import { playSfx } from '~/game/sfx'
import { theme } from '~/theme'

// The office's window manager frame, tuned with patterns proven in games:
//  - grip bar drags, with MAGNETIC SNAPPING to viewport edges and to other
//    windows (EVE/OpenTTD) — hold Alt to bypass;
//  - the grip carries the window TITLE + a pin (OpenTTD's sticky thumbtack:
//    pinned windows survive Esc-peel and the clutter cap);
//  - double-click the grip to COLLAPSE to a title bar (window-shade);
//  - unfocused windows go slightly TRANSPARENT so the office keeps living
//    through them (EVE; it IS the Glass Office) — hover restores;
//  - open/close pops with a soft scale + paper SFX (Animal Crossing juice),
//    disabled under prefers-reduced-motion.
// Panels keep their own headers/close buttons; injected !important CSS
// neutralizes their absolute positioning so panel components stay untouched.

// Pixel-material chrome: the frame is a 9-slice wood texture and the grip a
// wood plank, both generated at runtime in the furniture palette (no asset
// files) and rendered with image-rendering: pixelated — panels read as
// furniture OF the pixel world instead of websites floating over it.
let woodFrameUrl: string | null = null
let woodPlankUrl: string | null = null
function woodTextures(): { frame: string; plank: string } {
  if (woodFrameUrl && woodPlankUrl) return { frame: woodFrameUrl, plank: woodPlankUrl }
  const px = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    color: string,
  ) => {
    ctx.fillStyle = color
    ctx.fillRect(x, y, w, h)
  }
  // 9-slice frame: 30x30, 10px slices — bevelled wood with a dark seam.
  {
    const c = document.createElement('canvas')
    c.width = 30
    c.height = 30
    const ctx = c.getContext('2d')!
    px(ctx, 0, 0, 30, 30, '#1a120c') // hard outline
    px(ctx, 1, 1, 28, 28, '#5a4326') // wood body
    px(ctx, 1, 1, 28, 1, '#7d5836') // top highlight
    px(ctx, 1, 1, 1, 28, '#6b4a2e') // left light
    px(ctx, 1, 28, 28, 1, '#3a2c18') // bottom shadow
    px(ctx, 28, 1, 1, 28, '#42311d') // right shade
    // Grain flecks (deterministic).
    for (const [fx, fy] of [
      [5, 3], [12, 2], [21, 4], [3, 12], [27, 9], [2, 21], [26, 19], [9, 27],
      [17, 27], [24, 26],
    ]) {
      px(ctx, fx, fy, 2, 1, '#4a3524')
    }
    px(ctx, 10, 10, 10, 10, '#2a1f16') // centre slice (unused — content sits here)
    woodFrameUrl = c.toDataURL()
  }
  // Grip plank: 24x20 horizontal boards.
  {
    const c = document.createElement('canvas')
    c.width = 24
    c.height = 20
    const ctx = c.getContext('2d')!
    px(ctx, 0, 0, 24, 20, '#4a3524')
    px(ctx, 0, 0, 24, 1, '#6b4a2e')
    px(ctx, 0, 9, 24, 1, '#2f2214')
    px(ctx, 0, 10, 24, 1, '#5f4128')
    px(ctx, 0, 19, 24, 1, '#241a10')
    px(ctx, 7, 4, 3, 1, '#5f4128')
    px(ctx, 16, 14, 3, 1, '#3a2c18')
    woodPlankUrl = c.toDataURL()
  }
  return { frame: woodFrameUrl!, plank: woodPlankUrl! }
}

let injected = false
function injectFrameCss(): void {
  if (injected || typeof document === 'undefined') return
  injected = true
  const style = document.createElement('style')
  style.textContent = `
    .gw-win-content > * {
      position: static !important;
      inset: auto !important;
      width: 100% !important;
      max-height: 100% !important;
      border: none !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      flex: 1 1 auto;
      min-height: 0;
    }
    .gw-win-dim { opacity: 0.86; }
    .gw-win-dim.gw-win-pinned { opacity: 0.72; }
    .gw-win-dim:hover { opacity: 1; }
    @keyframes gw-pop-in {
      from { transform: scale(0.94); opacity: 0.3; }
      to { transform: scale(1); opacity: 1; }
    }
    .gw-win-frame { transition: opacity 0.15s ease; }
    @media (prefers-reduced-motion: no-preference) {
      .gw-win-frame { animation: gw-pop-in 0.16s ease-out; transform-origin: top center; }
    }
  `
  document.head.appendChild(style)
}

const SNAP = 12
const EDGE = 8
const TOP_MIN = 52

/** Magnetic targets: viewport edges + the other windows' rects. */
function snapPosition(
  winKey: string,
  x: number,
  y: number,
  width: number,
): { x: number; y: number } {
  let bestX = x
  let bestDx = SNAP
  let bestY = y
  let bestDy = SNAP
  const tryX = (target: number) => {
    const d = Math.abs(target - x)
    if (d < bestDx) {
      bestDx = d
      bestX = target
    }
  }
  const tryY = (target: number) => {
    const d = Math.abs(target - y)
    if (d < bestDy) {
      bestDy = d
      bestY = target
    }
  }
  tryX(EDGE)
  tryX(window.innerWidth - width - EDGE)
  tryY(TOP_MIN + 14)
  const others = document.querySelectorAll<HTMLElement>('[data-win-key]')
  others.forEach((el) => {
    if (el.dataset.winKey === winKey) return
    const r = el.getBoundingClientRect()
    tryX(r.left) // align left edges
    tryX(r.right + 6) // sit beside it
    tryX(r.left - width - 6) // sit on its other side
    tryX(r.right - width) // align right edges
    tryY(r.top) // align tops
    tryY(r.bottom + 6) // stack below it
  })
  return { x: bestX, y: bestY }
}

export function WindowFrame({
  winKey,
  title,
  accent,
  x,
  y,
  z,
  width,
  isTop,
  pinned,
  collapsed,
  onRaise,
  onMove,
  onTogglePin,
  onToggleCollapse,
  children,
}: {
  winKey: string
  title: string
  /** Per-kind hue for the strip under the grip (chat/tasks/notes…). */
  accent?: string
  x: number
  y: number
  z: number
  width: number
  isTop: boolean
  pinned: boolean
  collapsed: boolean
  onRaise: (key: string) => void
  onMove: (key: string, x: number, y: number) => void
  onTogglePin: (key: string) => void
  onToggleCollapse: (key: string) => void
  children: React.ReactNode
}) {
  injectFrameCss()
  const wood = woodTextures()
  const drag = useRef<{ dx: number; dy: number; id: number } | null>(null)

  // Open/close juice through the existing (opt-in) sound system.
  useEffect(() => {
    playSfx('open')
    return () => playSfx('close')
  }, [])

  const classes = [
    'gw-win-frame',
    !isTop ? 'gw-win-dim' : '',
    pinned ? 'gw-win-pinned' : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div
      data-win-key={winKey}
      className={classes}
      onPointerDownCapture={() => onRaise(winKey)}
      style={{
        position: 'absolute',
        left: x,
        top: y,
        width,
        zIndex: z,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: `calc(100vh - ${y + 16}px)`,
        background: theme.panel,
        border: '7px solid transparent',
        borderImage: `url(${wood.frame}) 10 round`,
        imageRendering: 'pixelated',
        boxShadow: isTop ? '0 18px 60px #000a' : '0 10px 30px #0007',
        overflow: 'hidden',
        ...(pinned ? { outline: '1px solid #7a5c26' } : null),
      }}
    >
      <div
        title="Drag to move · double-click to collapse"
        onDoubleClick={() => onToggleCollapse(winKey)}
        onPointerDown={(e) => {
          // Left/primary only: a right-button or second-finger "drag" would
          // hijack the window (and a swallowed pointerup gluing it to the
          // cursor); non-primary presses shouldn't move windows at all.
          if (!e.isPrimary || (e.pointerType === 'mouse' && e.button !== 0)) return
          drag.current = { dx: e.clientX - x, dy: e.clientY - y, id: e.pointerId }
          try {
            ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
          } catch {
            // Synthetic/stale pointers can't be captured — dragging still works.
          }
        }}
        onPointerMove={(e) => {
          if (!drag.current || drag.current.id !== e.pointerId) return
          let nx = Math.min(
            Math.max(EDGE, e.clientX - drag.current.dx),
            window.innerWidth - width - EDGE,
          )
          let ny = Math.min(
            Math.max(TOP_MIN, e.clientY - drag.current.dy),
            window.innerHeight - 90,
          )
          if (!e.altKey) {
            const snapped = snapPosition(winKey, nx, ny, width)
            nx = snapped.x
            ny = snapped.y
          }
          onMove(winKey, nx, ny)
        }}
        onPointerUp={(e) => {
          drag.current = null
          try {
            ;(e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId)
          } catch {
            // Nothing captured — fine.
          }
        }}
        onPointerCancel={() => {
          // System-cancelled touch (edge swipe, palm rejection) — without this
          // the stale drag glues the window to the next hover.
          drag.current = null
        }}
        onLostPointerCapture={() => {
          drag.current = null
        }}
        style={{
          flex: '0 0 auto',
          height: 20,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '0 6px 0 9px',
          cursor: 'grab',
          background: `url(${wood.plank})`,
          backgroundSize: '24px 20px',
          imageRendering: 'pixelated',
          userSelect: 'none',
          touchAction: 'none',
        }}
      >
        <span style={nail} />
        <span
          style={{
            flex: 1,
            fontSize: 10,
            fontWeight: 700,
            color: isTop ? '#efe9dc' : '#c9bfa8',
            textShadow: '0 1px 0 #1a120c',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            letterSpacing: 0.3,
          }}
        >
          {title}
        </span>
        {collapsed && (
          <button
            onClick={(e) => {
              e.stopPropagation()
              onToggleCollapse(winKey)
            }}
            onDoubleClick={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            title="Expand"
            style={gripBtn}
          >
            ▾
          </button>
        )}
        <button
          onClick={(e) => {
            e.stopPropagation()
            onTogglePin(winKey)
          }}
          onDoubleClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          title={
            pinned
              ? 'Unpin (pinned windows survive Esc and the window cap)'
              : 'Pin this window'
          }
          style={{
            ...gripBtn,
            color: pinned ? '#ffd28a' : theme.textDim,
            transform: pinned ? 'none' : 'rotate(45deg)',
          }}
        >
          📌
        </button>
        <span style={nail} />
      </div>
      {accent && (
        <div
          style={{
            flex: '0 0 auto',
            height: 2,
            background: accent,
            opacity: 0.55,
          }}
        />
      )}
      {/* Window-shade = HIDE, not unmount: unmounting destroyed composer
          drafts, drill-down state, and scroll positions on every collapse. */}
      <div
        className="gw-win-content"
        style={{ display: collapsed ? 'none' : 'flex', minHeight: 0 }}
      >
        {children}
      </div>
    </div>
  )
}

const nail: React.CSSProperties = {
  flex: '0 0 auto',
  width: 3,
  height: 3,
  background: '#c9a34a',
  boxShadow: '0 1px 0 #1a120c',
}

const gripBtn: React.CSSProperties = {
  flex: '0 0 auto',
  background: 'none',
  border: 'none',
  color: theme.textDim,
  cursor: 'pointer',
  fontSize: 10,
  lineHeight: 1,
  padding: '2px 3px',
}
